<template>
  <div class="max-w-3xl mx-auto">
    <h1 class="text-2xl font-bold mb-6">Create New Tool</h1>
    
    <div class="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 mb-8">
      <div class="p-6">
        <form @submit.prevent="createTool">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Tool Name</label>
            <input 
              v-model="tool.name" 
              class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              placeholder="e.g., Email Tone Converter"
              required
            />
          </div>
          
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
            <div class="relative">
              <textarea
                v-model="tool.description"
                rows="4"
                class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                placeholder="Describe what your tool does in a few sentences..."
                required
                @focus="showVoiceHint = !tool.description"
              ></textarea>
              <button
                type="button"
                @click="toggleSpeechRecognition"
                class="absolute right-2 bottom-2 p-2 rounded-full hover:bg-gray-100 transition-colors"
                title="Record description"
                :class="{'animate-pulse-subtle bg-blue-50': showVoiceHint}"
              >
                <i :class="[isRecording ? 'fas fa-stop text-red-500' : 'fas fa-microphone text-gray-500', 'text-lg']"></i>
              </button>
              
              <!-- Voice hint animation -->
              <div 
                v-if="!tool.description && !isRecording" 
                class="voice-hint absolute right-12 bottom-3 bg-blue-50 text-blue-600 px-3 py-1 rounded-lg text-sm max-w-[200px] shadow-sm border border-blue-100 transition-opacity"
              >
                <div class="flex items-center space-x-2">
                  <i class="fas fa-lightbulb text-yellow-400"></i>
                  <div>
                    <p>Try speaking!</p>
                    <div class="text-xs text-blue-500">Click the mic and describe your tool with your voice</div>
                  </div>
                </div>
                <div class="absolute -right-2 top-1/2 transform -translate-y-1/2 w-2 h-2 rotate-45 bg-blue-50 border-r border-t border-blue-100"></div>
              </div>
            </div>
            <div v-if="isRecording" class="mt-1 text-sm text-red-500 flex items-center">
              <span class="inline-block h-2 w-2 rounded-full bg-red-500 mr-2 animate-pulse"></span>
              Recording... Speak now
            </div>
          </div>
          
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select 
              v-model="tool.category" 
              class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              required
            >
              <option value="">Select a category...</option>
              <option value="Email">Email</option>
              <option value="Career">Career</option>
              <option value="Marketing">Marketing</option>
              <option value="Social Media">Social Media</option>
              <option value="Legal">Legal</option>
              <option value="Content">Content</option>
              <option value="Technical">Technical</option>
              <option value="Business">Business</option>
              <option value="SEO">SEO</option>
            </select>
          </div>
          
          <div class="mt-6">
            <button 
              type="submit" 
              class="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md"
              :disabled="isSaving"
            >
              {{ isSaving ? 'Creating Tool...' : 'Create Tool with AI' }}
            </button>
            <p class="text-sm text-gray-500 mt-2 text-center">
              We'll use AI to automatically generate your tool's configuration based on the name and description.
            </p>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import AppUtil from '../utils/AppUtil'

export default {
  name: 'NewToolForm',
  props: {
    service: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      model: nimbleai.model,
      tool: {
        name: '',
        description: '',
        category: ''
      },
      isSaving: false,
      isRecording: false,
      recognition: null,
      showVoiceHint: false,
    }
  },
  methods: {
    createTool() {
      this.model.loading = true
      
      const request = {
        tool: { ...this.tool }
      }
      
      this.service.create(request)
        .then((response) => {
          // Emit the created tool to the parent
          this.$emit('tool-created', response)
          // Then navigate to the edit screen
          this.$router.push({name: 'edit-tool', params: { id: response.slug }})
        })
        .catch((error) => {
          console.error('Error creating tool:', error)
          const errorMessages = AppUtil.parseError(error)
          errorMessages.forEach(message => {
            this.$toast.error(message, {
              position: 'top-right',
              duration: 5000,
              dismissible: true
            })
          })
        })
        .finally(() => {
          this.model.loading = false
        })
    },
    initSpeechRecognition() {
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        console.warn('Speech recognition not supported in this browser');
        return;
      }
      
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      this.recognition = new SpeechRecognition();
      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = 'en-US'; // You can make this configurable
      
      this.recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        if (finalTranscript) {
          // Append to existing text or replace it based on your preference
          this.tool.description = this.tool.description 
            ? this.tool.description + ' ' + finalTranscript
            : finalTranscript;
        }
      };
      
      this.recognition.onerror = (event) => {
        console.error('Speech recognition error', event.error);
        this.isRecording = false;
      };
      
      this.recognition.onend = () => {
        // Only set isRecording to false if the user manually stopped
        // or if there was an error (handled in onerror)
        if (this.isRecording) {
          // If it was still supposed to be recording, restart it
          try {
            this.recognition.start();
          } catch (e) {
            console.error('Could not restart recognition', e);
            this.isRecording = false;
          }
        }
      };
    },
    toggleSpeechRecognition() {
      if (!this.recognition) {
        this.initSpeechRecognition();
      }
      
      if (this.isRecording) {
        // Stop recording
        this.recognition.stop();
        this.isRecording = false;
      } else {
        // Start recording
        try {
          this.recognition.start();
          this.isRecording = true;
          this.showVoiceHint = false;
        } catch (e) {
          console.error('Error starting speech recognition', e);
        }
      }
    }
  },
  mounted() {
    // Initialize speech recognition when component mounts
    this.initSpeechRecognition();
  },
  beforeUnmount() {
    // Clean up speech recognition when component unmounts
    if (this.recognition) {
      this.recognition.stop();
    }
  },
  watch: {
    'tool.description': function(newVal) {
      // Hide the hint when text is entered
      if (newVal && this.showVoiceHint) {
        this.showVoiceHint = false;
      }
    }
  }
}
</script>

<style scoped>
.voice-hint {
  animation: fadeInSlide 0.5s ease-out;
}

@keyframes fadeInSlide {
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-pulse-subtle {
  animation: pulse-subtle 2s infinite;
}

@keyframes pulse-subtle {
  0%, 100% {
    background-opacity: 0.5;
    box-shadow: 0 0 0 rgba(59, 130, 246, 0);
  }
  50% {
    background-opacity: 1;
    box-shadow: 0 0 8px rgba(59, 130, 246, 0.3);
  }
}
</style> 